/**
 * @fileoverview Invitation service for frontend API calls
 * @module services/invitation-service
 * @description Handles invitation-related API communication
 */

import { z } from 'zod'
import { api } from '@/utils/api-client'
import { logger } from '@/lib/logger'

/**
 * Invitation interface matching backend
 */
export interface Invitation {
  id: string
  email: string
  role: string
  status: 'pending' | 'accepted' | 'declined' | 'expired'
  invitedBy: string
  invitedByName?: string
  workspaceName?: string
  createdAt: string
  expiredAt: string
  invitationId: string
}

/**
 * Invitation details for verification page
 */
export interface InvitationDetails {
  id: string
  email: string
  role: string
  status: string
  workspaceId: string
  workspaceName: string
  invitedByName: string
  expiredAt: string
  isExpired: boolean
}

interface InvitationAcceptance {
  selectedWorkspace: {
    id: string
  } | null
}

/**
 * Create invitation request
 */
export interface CreateInvitationRequest {
  email: string
  role: 'admin' | 'user'
}

/**
 * Validation schemas
 */
const createInvitationSchema = z.object({
  email: z.string().email('Please provide a valid email address'),
  role: z.enum(['admin', 'user'])
})

/**
 * Invitation service with static methods
 */
export class InvitationService {
  /**
   * Gets all workspace invitations
   */
  static async getWorkspaceInvitations(): Promise<Invitation[]> {
    try {
      const response = await api.get('/workspace/invitations', {
        requiresAuth: true,
        showErrorToast: false, // We'll handle errors in the component
      })
      
      if (response.success) {
        return response.data
      }
      
      return []
    } catch (error) {
      logger.error('Failed to fetch workspace invitations', error)
      throw error instanceof Error ? error : new Error('Failed to fetch invitations')
    }
  }

  /**
   * Creates a new invitation
   */
  static async createInvitation(invitationData: CreateInvitationRequest): Promise<Invitation> {
    try {
      // Validate input data
      const validatedData = createInvitationSchema.parse(invitationData)
      const response = await api.post('/workspace/invitations', validatedData)
      return response.data
    } catch (error) {
      logger.error('Failed to create invitation', error)
      
      if (error instanceof z.ZodError) {
        const fieldErrors = error.issues.map((err) => `${err.path.join('.')}: ${err.message}`).join(', ')
        throw new Error(`Invalid data: ${fieldErrors}`)
      }
      
      throw error instanceof Error ? error : new Error('Failed to create invitation')
    }
  }

  /**
   * Deletes an invitation
   */
  static async deleteInvitation(invitationId: string): Promise<void> {
    try {
      if (!invitationId) {
        throw new Error('Invitation ID is required')
      }

      await api.delete(`/workspace/invitations/${invitationId}`)
    } catch (error) {
      logger.error('Failed to delete invitation', error)
      throw error instanceof Error ? error : new Error('Failed to delete invitation')
    }
  }

  /**
   * Resends an invitation
   */
  static async resendInvitation(invitationId: string): Promise<Invitation> {
    try {
      if (!invitationId) {
        throw new Error('Invitation ID is required')
      }

      const response = await api.post(`/workspace/invitations/${invitationId}/resend`)
      return response
    } catch (error) {
      logger.error('Failed to resend invitation', error)
      throw error instanceof Error ? error : new Error('Failed to resend invitation')
    }
  }

  /**
   * Gets invitation details by invitation ID (public endpoint)
   */
  static async getInvitationDetails(invitationId: string): Promise<InvitationDetails> {
    try {
      if (!invitationId) {
        throw new Error('Invitation ID is required')
      }

      const response = await api.get(`/invitations/public/${invitationId}`, {
        requiresAuth: false
      })
      
      // Handle nested response structure
      if (response && typeof response === 'object' && 'data' in response) {
        return response.data
      }
      
      // Handle direct response (fallback for backward compatibility)
      return response as InvitationDetails
    } catch (error) {
      logger.error('Failed to get invitation details', error)
      throw error instanceof Error ? error : new Error('Failed to get invitation details')
    }
  }

  /**
   * Checks if a user exists by email (public endpoint)
   */
  static async checkUserExists(email: string): Promise<{exists: boolean}> {
    try {
      if (!email || !email.trim()) {
        throw new Error('Email is required')
      }

      const trimmedEmail = email.trim().toLowerCase()
      
      const response = await api.get(`/auth/check-user/${encodeURIComponent(trimmedEmail)}`, {
        requiresAuth: false
      })
      
      
      // Handle the nested response structure
      if (response && typeof response === 'object' && response.success && response.data && typeof response.data === 'object') {
        const exists = Boolean(response.data.exists)
        return { exists }
      }
      
      // Handle direct response structure (fallback)
      if (response && typeof response === 'object' && 'exists' in response) {
        const exists = Boolean((response as any).exists)
        return { exists }
      }
      

      return { exists: false }
    } catch (error) {
      logger.error('Failed to check user existence', error)
      // If the API endpoint doesn't exist or fails, assume user doesn't exist (safer for new user flow)
      return { exists: false }
    }
  }

  /**
   * Accepts an invitation for the currently authenticated user.
   * The backend derives the accepting email from the session, not from the client,
   * so this must be called only after a successful sign-in or sign-up.
   */
  static async acceptInvitation(invitationId: string): Promise<InvitationAcceptance> {
    try {
      if (!invitationId) {
        throw new Error('Invitation ID is required')
      }

      const response = await api.post(`/invitations/${invitationId}/accept`, {}, {
        requiresAuth: true,
        showErrorToast: false,
      })
      return response.data
    } catch (error) {
      logger.error('Failed to accept invitation', error)
      throw error instanceof Error ? error : new Error('Failed to accept invitation')
    }
  }

  /**
   * Generates invitation URL for sharing
   */
  static generateInvitationUrl(invitationId: string): string {
    const baseUrl = window.location.origin
    return `${baseUrl}/invitation/${invitationId}`
  }

  /**
   * Formats expiry date to human readable string
   */
  static formatExpiryDate(expiredAt: string): string {
    if (!expiredAt) {
      return 'No expiry date'
    }
    
    const date = new Date(expiredAt)
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      logger.error('Invalid date format', expiredAt)
      return 'Invalid date'
    }
    
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays < 0) {
      return 'Expired'
    } else if (diffDays === 0) {
      return 'Expires today'
    } else if (diffDays === 1) {
      return 'Expires tomorrow'
    } else if (diffDays <= 7) {
      return `Expires in ${diffDays} days`
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }
  }

  /**
   * Checks if invitation is expired
   */
  static isInvitationExpired(expiredAt: string): boolean {
    if (!expiredAt) {
      return false
    }
    
    const date = new Date(expiredAt)
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      logger.error('Invalid date format', expiredAt)
      return false
    }
    
    return new Date() > date
  }
}

/**
 * Export default for easier imports
 */
export default InvitationService
